import React, { useRef } from 'react';
import { Trash2 } from 'lucide-react';
import { useFocusTrap } from '../../../hooks/useFocusTrap';

interface DeleteRecurringDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDeleteSingle: () => void;
  onDeleteSeries: () => void;
  eventTitle: string;
}

export default function DeleteRecurringDialog({
  isOpen,
  onClose,
  onDeleteSingle,
  onDeleteSeries,
  eventTitle
}: DeleteRecurringDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeInBackdrop">
      <div ref={dialogRef} role="dialog" aria-modal="true" className="bg-[var(--modal-bg)] rounded-xl w-full max-w-md p-6 shadow-xl animate-fadeIn">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-[var(--error)]/10 rounded-lg">
            <Trash2 className="w-5 h-5 text-[var(--error)]" />
          </div>
          <h3 className="text-lg font-semibold text-[var(--content-primary)]">Delete Recurring Event</h3>
        </div>

        <div className="mb-6">
          <p className="text-[var(--content-secondary)] mb-2">
            "{eventTitle}" is a recurring event.
          </p>
          <p className="text-sm text-[var(--content-tertiary)]">
            Would you like to delete only this occurrence or all events in the series?
          </p>
        </div>

        <div className="space-y-2">
          <button
            onClick={onDeleteSingle}
            className="w-full px-4 py-3 text-left border border-[var(--border-primary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
          >
            <div className="font-medium text-[var(--content-primary)]">Delete this event only</div>
            <div className="text-sm text-[var(--content-tertiary)]">Other events in the series will remain</div>
          </button>

          <button
            onClick={onDeleteSeries}
            className="w-full px-4 py-3 text-left border border-[var(--error)]/30 bg-[var(--error)]/10 rounded-lg hover:bg-[var(--error)]/20 transition-colors"
          >
            <div className="font-medium text-[var(--error)]">Delete all events in series</div>
            <div className="text-sm text-[var(--error)]/80">This will delete all recurring instances</div>
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full mt-4 px-4 py-2 bg-[var(--surface-tertiary)] text-[var(--content-secondary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
